import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from 'App/shadcn/ui/dropdown-menu';
import AgentAvatar from '../components/AgentAvatar';

// Who a new chat is with: the personal assistant (id null) or one of the
// agents. Sits in the composer toolbar on the empty new-chat screen only; an
// existing conversation keeps the agent it was started with.

type PickerAgent = { id: number; name: string; avatarUrl: string | null; role?: string | null };

type Props = {
	/** The personal assistant; picking it is `null`. `id` only colours its avatar. */
	assistant: PickerAgent;
	agents: PickerAgent[];
	/** Null is the assistant. Passed whole, since a chat can be with an agent the menu leaves out. */
	selected: PickerAgent | null;
	onSelect: (agentId: number | null) => void;
};

const ChatAgentPicker = ({ assistant, agents, selected, onSelect }: Props) => {
	const selectedId = selected?.id ?? null;
	const current = selected ?? assistant;

	const renderItem = (agent: PickerAgent, value: number | null) => (
		<DropdownMenuItem key={value ?? 'assistant'} onSelect={() => value !== selectedId && onSelect(value)}>
			<AgentAvatar agent={agent} size={20} />
			<span className="flex min-w-0 flex-1 flex-col">
				<span className="truncate">{agent.name}</span>
				{agent.role && <span className="truncate text-xs text-muted-foreground">{agent.role}</span>}
			</span>
			{value === selectedId && <Check className="h-4 w-4 opacity-60" />}
		</DropdownMenuItem>
	);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<button type="button" className="ws-chat-agent-picker">
					<AgentAvatar agent={current} size={18} />
					<span>{current.name}</span>
					<ChevronDown style={{ width: 14, height: 14, opacity: 0.6 }} />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
				{renderItem(assistant, null)}
				<DropdownMenuSeparator />
				{agents.map(a => renderItem(a, a.id))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ChatAgentPicker;
